/**
 * QMS Column - Droppable column for Kanban board
 * Contains draggable ticket cards
 */

import { useTranslation } from 'react-i18next';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { QmsColumn as QmsColumnType, QmsTicket } from '../../types/qms.types';
import { QmsTicketCard } from './QmsTicketCard';

interface QmsColumnProps {
  column: QmsColumnType;
  tickets: QmsTicket[];
  onEditTicket: (ticket: QmsTicket) => void;
}

export function QmsColumn({ column, tickets, onEditTicket }: QmsColumnProps) {
  const { t } = useTranslation('qms');
  const { setNodeRef, isOver } = useDroppable({
    id: column.key,
  });

  return (
    <div className="flex flex-col bg-gray-50 rounded-lg min-w-[240px] sm:min-w-[280px] max-w-[280px] sm:max-w-[320px] flex-shrink-0">
      {/* Column header */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-sm text-gray-900">{column.title}</h2>
          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-medium">
            {tickets.length}
          </span>
        </div>
      </div>

      {/* Column content - droppable area */}
      <div
        ref={setNodeRef}
        className={`flex-1 p-2 space-y-2 overflow-y-auto min-h-[200px] ${
          isOver ? 'bg-blue-50' : ''
        }`}
      >
        <SortableContext
          items={tickets.map((t) => t._id)}
          strategy={verticalListSortingStrategy}
        >
          {tickets.map((ticket) => (
            <QmsTicketCard key={ticket._id} ticket={ticket} onEdit={onEditTicket} />
          ))}

          {tickets.length === 0 && (
            <div className="text-center text-sm text-gray-400 py-8">
              {t('kanban.noTickets')}
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}
